import React from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ComparisonBadge from './ComparisonBadge';

/**
 * MetricCard - Componente reutilizable para mostrar métricas con comparación
 * @param {string} title - Título de la métrica
 * @param {number} value - Valor principal
 * @param {number} previousValue - Valor anterior (opcional)
 * @param {string} format - Tipo de formato: 'currency', 'number', 'percentage'
 * @param {string} variant - Color de la tarjeta: 'primary', 'success', 'warning', 'danger', 'info'
 * @param {string} icon - Ícono opcional (emoji o clase de icono)
 * @param {string} subtitle - Subtítulo opcional
 * @param {boolean} inverse - Si true, invierte los colores (menor es mejor)
 */
const MetricCard = ({
    title,
    value,
    previousValue,
    format = 'number',
    variant = 'primary',
    icon,
    subtitle,
    inverse = false
}) => {
    const { i18n } = useTranslation();

    const formatValue = (val) => {
        if (val === null || val === undefined) return '-';

        switch (format) {
            case 'currency':
                return '$' + parseFloat(val).toLocaleString(i18n.language, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    style: 'decimal'
                });
            case 'percentage':
                return `${parseFloat(val).toFixed(2)}%`;
            case 'number':
            default:
                return parseFloat(val).toLocaleString(i18n.language);
        }
    };

    // Determine card background based on variant
    const getCardClass = () => {
        switch (variant) {
            case 'success':
                return 'text-white bg-success';
            case 'warning':
                return 'text-white bg-warning';
            case 'danger':
                return 'text-white bg-danger';
            case 'info':
                return 'text-white bg-info';
            case 'primary':
            default:
                return '';
        }
    };

    return (
        <Card className={`text-center ${getCardClass()}`}>
            <Card.Body>
                {icon && <div className="mb-2" style={{ fontSize: '2rem' }}>{icon}</div>}
                <Card.Title as="h4" className="mb-1">
                    {formatValue(value)}
                </Card.Title>
                <Card.Text className="mb-2">
                    {title}
                </Card.Text>
                {previousValue !== undefined && previousValue !== null && (
                    <ComparisonBadge
                        current={value}
                        previous={previousValue}
                        format={format}
                        inverse={inverse}
                    />
                )}
                {subtitle && (
                    <Card.Text className="mt-2 small">
                        {subtitle}
                    </Card.Text>
                )}
            </Card.Body>
        </Card>
    );
};

export default MetricCard;
